'use client';

import { accountsService } from '@/@account/services';
import { ADMIN_ROLE } from '@/@account/types/constants';
import {
    useCreditBalance,
    useCreditReadTarget,
} from '@/@credit-consumption/hooks/useCreditLedgerReads';
import { creditConsumptionService } from '@/@credit-consumption/services';
import {
    ALERT_THRESHOLDS,
    QUERY_KEYS,
    resolveWarnThresholdPct,
} from '@/@credit-consumption/types/constants';
import type { ICreditConsumptionSummary } from '@/@credit-consumption/types/entities';
import type { PauseCopy, PauseReason, TrialStripCopy } from '@/lib/credits/credit-vocabulary';
import { useSession } from '@/providers/enhanced-session';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { useStudioAI } from '../context';
import { ledgerChatAlerts, type ChatCreditAudience } from '../utils/ledger-chat-alerts';
import { creditPausePlaceholder } from '../utils/user-spend-limit-copy';

export interface StudioCreditAlertState {
    /** Org is subject to credit gate (trial org access, not freemium / per-user license). */
    showAlerts: boolean;
    /**
     * Which balance the alerts come from: `ledger` for an org on the new balance (the balance
     * response's `reader`), else `legacy`, today's alerts from the summary, unchanged.
     */
    reader: 'ledger' | 'legacy';
    isPaused: boolean;
    pauseReason: PauseReason | null;
    /** Ledger orgs: the pause banner's copy (`pauseCopy`), ending "See credits". */
    pause: PauseCopy | null;
    /** The input's placeholder while paused; null when not paused. */
    pausePlaceholder: string | null;
    /** Ledger orgs: the dismissible pace warning. */
    paceWarning: string | null;
    /** Ledger orgs on a trial: the strip under the input, in place of the old counter. */
    trialStrip: TrialStripCopy | null;
    userSpendLimit?: number;
    userMonthToDate?: number;
    resetDate?: string;
    licenseEndDate?: string | null;
    isDaniWarning: boolean;
    isPoolWarning: boolean;
    summary: ICreditConsumptionSummary | undefined;
    creditsRemaining: number;
    estimatedPct: number;
    poolPct: number;
    clearCreditGate: () => void;
}

/**
 * Credit consumption alerts for Studio AI chat surfaces.
 *
 * Switches on the balance response's `reader` (M4S-5895 plan step 27, docs/pdlc/credits-ui.md §7
 * "Chat"). An org on the new balance (`ledger`) takes its pause, pace warning and trial strip from
 * the balance (`ledgerChatAlerts`); under View-as that is the client's balance (`view=client`),
 * never the staff member's. Every other org, and every org while the balance cannot be read (the
 * ledger migration not yet run, an older API), keeps today's alerts: synced + token-estimated
 * usage from the summary API, the monthly cap with its reset date, merged with live credit-gate
 * denials from the chat stream.
 */
export function useStudioCreditAlerts(): StudioCreditAlertState {
    const { creditGateData, clearCreditGate } = useStudioAI();
    const { data: session } = useSession();

    const isFreemium = session?.user?.isUnaffiliated ?? false;
    const shouldFetchSummary = !isFreemium && !!session?.user;

    const { data: summary } = useQuery({
        queryKey: QUERY_KEYS.SUMMARY(),
        queryFn: () => creditConsumptionService.getSummary(),
        enabled: shouldFetchSummary,
        staleTime: creditGateData ? 0 : 30_000,
        refetchOnWindowFocus: true,
    });

    // The balance is read only where it can change the answer. In client mode that is an org the
    // summary already says is on the new balance (its `ledger` block, sent by the same one rule),
    // so with every switch off the chat surfaces make no request and no ledger read beyond
    // today's summary. Under View-as (staff only) the summary is the staff member's own, so the
    // client's balance is read to learn its reader.
    const target = useCreditReadTarget();
    const viewAs = target?.kind === 'org';
    const {
        data: balance,
        dataUpdatedAt: balanceUpdatedAt,
        isFetching: balanceFetching,
        refetch: refetchBalance,
    } = useCreditBalance(target, shouldFetchSummary && (viewAs || summary?.ledger != null));
    const onLedger = balance?.reader === 'ledger';

    // Members are pointed to their org admin (§7 "Contact"). Read only for a client on the new
    // balance; staff (and staff under View-as, who see the client admin's view, D13) read as admins.
    // A profile that loaded without the admin role is a member (D18: no org_role row counts as a
    // member); only a profile not read yet (loading or failed) falls back to the admin wording.
    const isInternal = session?.user?.isInternal === true;
    const { data: profile } = useQuery<{ role?: string } | undefined>({
        queryKey: ['accounts', 'userProfile'],
        queryFn: () => accountsService.getUserProfile(),
        enabled: onLedger && !viewAs && !isInternal && !!session?.user?.id,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const audience: ChatCreditAudience =
        viewAs || isInternal || !profile || profile.role === ADMIN_ROLE ? 'admin' : 'member';

    // A gate that arrives on a ledger org re-reads the balance; the gate clears only on an answer
    // read after it arrived, so the balance from before the 403 never clears it.
    const gateAt = useRef(0);
    useEffect(() => {
        if (!creditGateData) return;
        gateAt.current = Date.now();
        if (onLedger) void refetchBalance();
    }, [creditGateData, onLedger, refetchBalance]);

    useEffect(() => {
        if (!onLedger || !balance || !creditGateData || balanceFetching) return;
        if (creditGateData.reason === 'user_spend_limit_exceeded') return;
        if (balanceUpdatedAt <= gateAt.current) return;
        if (!balance.pause.paused && balance.available > 0) clearCreditGate();
    }, [onLedger, balance, balanceUpdatedAt, balanceFetching, creditGateData, clearCreditGate]);

    useEffect(() => {
        if (onLedger || !summary || !creditGateData) return;

        // The org cap can be unlimited while this user's own limit is spent.
        // Clearing here is what made the chat look like it returned nothing.
        if (creditGateData.reason === 'user_spend_limit_exceeded') return;

        // no_credits / license_expired are authoritative gate denials — clear only
        // when the summary shows recovery, not when monthly % drifts under danger.
        if (creditGateData.reason === 'no_credits') {
            if ((summary.totalPool.creditsRemaining ?? 0) > 0) {
                clearCreditGate();
            }
            return;
        }
        if (creditGateData.reason === 'license_expired') {
            if (summary.licenseExpired !== true) {
                clearCreditGate();
            }
            return;
        }

        const wouldBlock = summary.daniCompute.wouldBlockDaniChat ?? false;
        const syncedUnderCap = summary.daniCompute.percentUsed < ALERT_THRESHOLDS.DANI_DANGER;
        if (!wouldBlock && syncedUnderCap) {
            clearCreditGate();
        }
    }, [onLedger, summary, creditGateData, clearCreditGate]);

    return useMemo(() => {
        if (onLedger && balance) {
            const ledger = ledgerChatAlerts({
                balance,
                gate: creditGateData,
                audience,
                now: new Date(),
            });
            return {
                showAlerts:
                    shouldFetchSummary &&
                    (ledger.isPaused || ledger.paceWarning != null || ledger.trialStrip != null),
                reader: 'ledger',
                isPaused: ledger.isPaused,
                pauseReason: ledger.pauseReason,
                pause: ledger.pause,
                pausePlaceholder: ledger.placeholder,
                paceWarning: ledger.paceWarning,
                trialStrip: ledger.trialStrip,
                resetDate: creditGateData?.resetDate ?? balance.pause.resetDate ?? undefined,
                licenseEndDate: creditGateData?.licenseEndDate ?? null,
                isDaniWarning: false,
                isPoolWarning: false,
                summary,
                creditsRemaining: balance.available,
                estimatedPct: 0,
                poolPct: 0,
                userSpendLimit: creditGateData?.userSpendLimit,
                userMonthToDate: creditGateData?.userMonthToDate,
                clearCreditGate,
            };
        }

        const monthlyCap =
            summary?.daniCompute.effectiveMonthlyCap ?? summary?.daniCompute.monthlyCap ?? 0;
        const annualPool = summary?.totalPool.totalPool ?? 0;
        const licenseExpired = summary?.licenseExpired === true;
        // Keep gate denials (e.g. no_credits with zero caps) visible until cleared.
        const hasActiveGate = !!creditGateData;
        const hasCreditLimits = monthlyCap > 0 || annualPool > 0 || licenseExpired || hasActiveGate;
        const showAlerts = shouldFetchSummary && hasCreditLimits;

        const syncedPct = summary?.daniCompute.percentUsed ?? 0;
        const estimatedPct = summary?.daniCompute.estimatedPercentUsed ?? syncedPct;
        const creditsRemaining =
            summary?.daniCompute.estimatedCreditsRemaining ??
            summary?.daniCompute.creditsRemaining ??
            0;
        const resetDate = creditGateData?.resetDate ?? summary?.daniCompute.capResetDate;
        const licenseEndDate =
            creditGateData?.licenseEndDate ?? summary?.totalPool.contractExpiry ?? null;

        const poolPct =
            summary && summary.totalPool.totalPool > 0
                ? summary.totalPool.totalConsumed / summary.totalPool.totalPool
                : 0;

        const wouldBlockFromSummary = summary?.daniCompute.wouldBlockDaniChat ?? false;

        const isPausedFromSummary = showAlerts && wouldBlockFromSummary;

        const isAnnualPoolExhausted =
            showAlerts &&
            !!summary &&
            summary.totalPool.totalPool > 0 &&
            summary.totalPool.creditsRemaining <= 0;

        const isPausedFromGate = !!creditGateData;
        const isPaused =
            isPausedFromGate || isPausedFromSummary || isAnnualPoolExhausted || licenseExpired;

        const pauseReason: StudioCreditAlertState['pauseReason'] = creditGateData?.reason
            ? creditGateData.reason
            : licenseExpired
              ? 'license_expired'
              : isAnnualPoolExhausted
                ? 'annual_pool_exceeded'
                : isPausedFromSummary
                  ? 'monthly_cap_exceeded'
                  : null;

        const warnThresholdPct = resolveWarnThresholdPct(summary?.warnThresholdPct);
        const isDaniWarning =
            showAlerts &&
            monthlyCap > 0 &&
            estimatedPct >= warnThresholdPct &&
            estimatedPct < ALERT_THRESHOLDS.DANI_DANGER &&
            !isPaused;

        const isPoolWarning =
            showAlerts && poolPct >= ALERT_THRESHOLDS.POOL_WARNING && poolPct < 1 && !isPaused;

        return {
            showAlerts,
            reader: 'legacy',
            isPaused,
            pauseReason,
            pause: null,
            pausePlaceholder: isPaused ? creditPausePlaceholder(pauseReason) : null,
            paceWarning: null,
            trialStrip: null,
            resetDate,
            licenseEndDate,
            isDaniWarning,
            isPoolWarning,
            summary,
            creditsRemaining,
            estimatedPct,
            poolPct,
            userSpendLimit: creditGateData?.userSpendLimit,
            userMonthToDate: creditGateData?.userMonthToDate,
            clearCreditGate,
        };
    }, [onLedger, balance, audience, shouldFetchSummary, summary, creditGateData, clearCreditGate]);
}
